import React, { useState, useEffect } from 'react';
import './Dashboard.scss';
import DashBoardIcon from '../../assets/images/newDashboardWordIcon.png';
import { NavLink } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList } from 'recharts';
import { formatDateWithTime, formatDateWith12HourTime, formatDateTime12Hour } from '../../shared/utils/dateUtils';
import { getWeeklyDailySummaries } from '../../core/API/workplace-daily-summaries';
import {
	CircularProgress,
	Grid,
	Box,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
} from '@mui/material';
import { getCategorizedToDos } from '../../core/API/todo';
import { getCategorizedChecklistScheduleEvents } from '../../core/API/checklist';
import { LocalEvents } from '../../shared/components/LocalEvents';
import { LocalEvent } from '../../core/API/local-events';
import { HotGame } from '../../core/API/hotGames';
import EventDetailView from '../../shared/components/EventDetailView/EventDetailView';
import { usePermissionCheck } from '../../core/hooks/usePermissionCheck';
import { isChecklistEventAssignedToCurrentUser } from '../../core/utils/checklistEventAssignment';

import HotGames from '../../components/featured/hot-games/HotGames';

const formatDate = (dateString: string) => {
	return new Date(dateString).toLocaleDateString('en-US', {
		month: '2-digit',
		day: '2-digit',
		year: 'numeric',
	});
};

const formatDateTime = (dateString: string) => {
	return formatDateWith12HourTime(dateString);
};

// Interface for checklist events
interface ChecklistScheduleEvent {
	id: number;
	checklistTemplate: {
		label: string;
		version: number;
		type?: string;
	};
	checklistSchedule?: {
		id: number;
		assignmentType: string;
		assignee?: {
			id: number;
			firstName: string;
			middleName?: string;
			lastName: string;
		} | null;
		group?: {
			id: number;
			label: string;
		} | null;
	};
	workplace?: {
		id: number;
		name: string;
	};
	organization?: {
		id: number;
		name: string;
	} | null;
	scheduledDate: string;
	scheduledTime?: string;
	scheduledDateFormatted?: string;
	scheduledTimeFormatted?: string;
	status: string;
	completedBy: any;
	completedAt: any;
}

interface Todo {
	id: number;
	description?: string;
	label?: string;
	name?: string;
	title?: string;
	note?:
		| string
		| {
				message?: string;
				description?: string;
		  };
	status?: {
		name?: string;
		label?: string;
	};
	priority?: {
		name?: string;
		label?: string;
	};
	user?: {
		id: number;
		firstName: string;
		middleName?: string;
		lastName: string;
	};
	dueDate?: string;
	due_date?: string;
	dueTime?: string;
	pastDue?: boolean;
}

const Dashboard = () => {
	const { hasModuleViewPermission } = usePermissionCheck();
	const canViewDashboard = hasModuleViewPermission('Dashboard');
	const canViewChecklistAll = hasModuleViewPermission('Checklist - All');
	const canViewChecklistMy = hasModuleViewPermission('Checklist - My');
	const canViewToDoAll = hasModuleViewPermission('To-Do - All');
	const canViewToDoMy = hasModuleViewPermission('To-Do - My');

	const [upcomingChecklists, setUpcomingChecklists] = useState<ChecklistScheduleEvent[]>([]);
	const [completedChecklists, setCompletedChecklists] = useState<ChecklistScheduleEvent[]>([]);
	const [allOutstandingTodos, setAllOutstandingTodos] = useState<Todo[]>([]);
	const [loading, setLoading] = useState(true);
	const workplaceId = Number(localStorage.getItem('workplace-id') || 1);
	const workplacePostcode = localStorage.getItem('workplace-postcode');

	// Event detail view state
	const [selectedEvent, setSelectedEvent] = useState<LocalEvent | HotGame | null>(null);
	const [eventType, setEventType] = useState<'local-event' | 'hot-game' | null>(null);
	const [showEventDetail, setShowEventDetail] = useState(false);

	// Helper function to get description from various possible fields
	const getDescriptionDisplay = (todo: Todo): string => {
		const noteValue =
			typeof todo.note === 'string' ? todo.note : todo.note?.message || todo.note?.description;

		const candidates = [todo?.description, todo?.label, todo?.name, todo?.title, noteValue];
		return candidates.find((v) => typeof v === 'string' && v.trim() !== '') || 'N/A';
	};

	// Helper function to format todo date with time
	const formatTodoDateWithTime = (todo: Todo): string => {
		const dueDateRaw = todo.due_date ?? todo.dueDate;
		const dueTime = todo.dueTime;
		return formatDateTime12Hour(dueDateRaw, dueTime);
	};

	// Helper function to get assigned display for checklist
	const getChecklistAssignedDisplay = (checklist: ChecklistScheduleEvent): string => {
		const schedule = checklist.checklistSchedule;
		if (!schedule) return 'N/A';

		// If assignment type is group, show group label
		if (schedule.assignmentType === 'group') {
			return schedule.group?.label || 'Unassigned Group';
		}

		// If assignment type is assignee, show assignee's firstName and lastName
		if (schedule.assignmentType === 'assignee') {
			return schedule.assignee
				? `${schedule.assignee.firstName} ${schedule.assignee.lastName}`.trim()
				: 'Unassigned';
		}

		// Fallback for any other assignment type
		return 'Unassigned';
	};

	// Helper function to get owner display for checklist
	const getChecklistOwnerDisplay = (checklist: ChecklistScheduleEvent): string => {
		if (checklist.checklistTemplate?.type === 'organization') {
			return checklist.organization?.name || 'Organization';
		}
		return checklist.workplace?.name || 'Workplace';
	};

	// Helper function to get assigned display for todo
	const getTodoAssignedDisplay = (todo: Todo): string => {
		if (todo.user) {
			const { firstName, middleName, lastName } = todo.user;
			return `${firstName}${middleName ? ' ' + middleName : ''} ${lastName}`;
		}
		return 'N/A';
	};

	// Helper function to get status display for todo
	const getTodoStatusDisplay = (todo: Todo): string => {
		return todo.status?.label || todo.status?.name || 'N/A';
	};

	// Event click handlers
	const handleLocalEventClick = (event: LocalEvent) => {
		setSelectedEvent(event);
		setEventType('local-event');
		setShowEventDetail(true);
	};

	const handleHotGameClick = (game: HotGame) => {
		setSelectedEvent(game);
		setEventType('hot-game');
		setShowEventDetail(true);
	};

	const handleCloseEventDetail = () => {
		setShowEventDetail(false);
		setSelectedEvent(null);
		setEventType(null);
	};

	// Helper function to get current user ID
	const getCurrentUserId = (): number | null => {
		try {
			const app = localStorage.getItem('BYOD-app')
				? JSON.parse(localStorage.getItem('BYOD-app') as string)
				: null;
			const idFromApp =
				app && app.user && (app.user.id || app.user['@id']) ? app.user.id || app.user['@id'] : null;
			if (idFromApp) return Number(idFromApp);
			const idFromLS = localStorage.getItem('userId');
			if (idFromLS) return Number(idFromLS);
		} catch (e) {
			// ignore
		}
		return null;
	};

	// Helper function to check if checklist is assigned to current user
	const isAssignedToCurrentUser = (checklist: ChecklistScheduleEvent): boolean =>
		isChecklistEventAssignedToCurrentUser(checklist, getCurrentUserId());

	// Filter checklists based on permissions
	const checklistsToShow = canViewChecklistAll
		? upcomingChecklists
		: canViewChecklistMy
			? upcomingChecklists.filter(isAssignedToCurrentUser)
			: [];
	// ('Checklists to show:', checklistsToShow);
	// Fetch checklist and todo data when component mounts
	useEffect(() => {
		const fetchData = async () => {
			try {
				const currentDate = new Date().toISOString().split('T')[0];

				// Fetch checklists using ChecklistService
				const checklistData = await getCategorizedChecklistScheduleEvents(currentDate, workplaceId);

				// Fetch todos using categorized API
				const todosResponse = await getCategorizedToDos({ workplaceId, page: 1, itemsPerPage: 10 });
				const myTodosData = Array.isArray(todosResponse?.my_todos) ? todosResponse.my_todos : [];
				const outstandingTodosData = Array.isArray(todosResponse?.outstanding_todos) ? todosResponse.outstanding_todos : [];
				const allOutstandingTodosData = canViewToDoAll
					? outstandingTodosData
					: canViewToDoMy
						? myTodosData
						: [];

				// Update state
				setUpcomingChecklists(checklistData?.myOutstanding || []);
				setCompletedChecklists(checklistData?.completed || []);
				setAllOutstandingTodos(allOutstandingTodosData);
			} catch (error) {
				console.error('Error fetching data:', error);
			} finally {
				setLoading(false);
			}
		};

		fetchData();
	}, [workplaceId]);

	const [salesData, setSalesData] = useState<{ day: string; sales: number; customers: number }[]>(
		[]
	);

	// Fetch sales and traffic data
	useEffect(() => {
		const fetchSalesData = async () => {
			try {
				// Generate last 7 days excluding today
				const last7Days = Array.from({ length: 7 }, (_, i) => {
					const date = new Date();
					date.setDate(date.getDate() - (7 - i));
					return {
						date: date.toISOString().split('T')[0],
						day: date.toLocaleDateString('en-US', { weekday: 'short' }),
						sales: 0,
						customers: 0,
					};
				});

				const response = await getWeeklyDailySummaries(workplaceId);

				// Merge API data with the 7-day template
				const mergedData = last7Days.map((day) => {
					const apiData = response.member?.find((item) => item.date === day.date);
					return {
						day: day.day,
						sales: apiData?.salesTotal || 0,
						customers: apiData?.trafficTotal || 0,
					};
				});

				setSalesData(mergedData);
			} catch (error) {
				console.error('Error fetching sales data:', error);
				// Still set 7 days with zero data on error (excluding today)
				const last7Days = Array.from({ length: 7 }, (_, i) => {
					const date = new Date();
					date.setDate(date.getDate() - (7 - i));
					return {
						day: date.toLocaleDateString('en-US', { weekday: 'short' }),
						sales: 0,
						customers: 0,
					};
				});
				setSalesData(last7Days);
			}
		};

		fetchSalesData();
	}, [workplaceId]);

	// Check permissions
	if (!canViewDashboard) {
		return (
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					minHeight: '400px',
					p: 3,
				}}
			>
				<Typography variant="h6" color="textSecondary">
					You do not have permission to view the Dashboard.
				</Typography>
			</Box>
		);
	}

	// Show event detail view if an event is selected
	if (showEventDetail && selectedEvent && eventType) {
		return (
			<EventDetailView
				event={selectedEvent}
				onClose={handleCloseEventDetail}
				eventType={eventType}
			/>
		);
	}

	return (
		<div className="dashboard-container">
			{/* Top Section */}
			<div className="top-section">
				<Grid container spacing={2} sx={{ height: '100%' }}>
					<Grid item xs={12} md={8}>
						<div className="card sales-chart" style={{ height: '100%' }}>
							<Typography variant="h6" sx={{ py: 2, m: 0, fontWeight: 500, fontSize: '20px' }}>
								Rolling 7 Days
							</Typography>
							<div className="charts-container">
								<div className="chart-section">
									<Typography
										variant="h6"
										sx={{ textAlign: 'center', m: 0, mb: 1, fontWeight: 500, fontSize: '20px' }}
									>
										Daily Sales ($)
									</Typography>
									<ResponsiveContainer width="100%" height={250}>
										<BarChart
											data={salesData}
											margin={{
												top: 30,
												right: 30,
												left: 20,
												bottom: 5,
											}}
										>
											<XAxis dataKey="day" />
											<YAxis />
											<Tooltip />
											<Bar dataKey="sales" fill="#436183">
												<LabelList
													dataKey="sales"
													position="top"
													formatter={(value) => `$${value ?? 0}`}
													style={{ fontSize: '12px' }}
												/>
											</Bar>
										</BarChart>
									</ResponsiveContainer>
								</div>

								<div className="chart-section">
									<Typography
										variant="h6"
										sx={{ textAlign: 'center', m: 0, mb: 1, fontWeight: 500, fontSize: '20px' }}
									>
										Daily Customers
									</Typography>
									<ResponsiveContainer width="100%" height={250}>
										<BarChart
											data={salesData}
											margin={{
												top: 30,
												right: 30,
												left: 20,
												bottom: 5,
											}}
										>
											<XAxis dataKey="day" />
											<YAxis />
											<Tooltip />
											<Bar dataKey="customers" fill="#436183">
												<LabelList
													dataKey="customers"
													position="top"
													formatter={(value) => value ?? 0}
													style={{ fontSize: '12px' }}
												/>
											</Bar>
										</BarChart>
									</ResponsiveContainer>
								</div>
							</div>
							<style>
								{`
							.charts-container {
								display: flex;
								flex-direction: column;
								gap: 20px;
								padding: 15px;
							}
							.chart-section {
								flex: 1;
							}
							.chart-section h3 {
								text-align: center;
								margin-bottom: 10px;
								color: #333;
							}
						`}
							</style>
						</div>
					</Grid>
					<Grid item xs={12} md={4}>
						<Grid
							container
							direction="column"
							spacing={2}
							sx={{
								height: '102%',
								'& > .MuiGrid-item': {
									flex: 1,
									minHeight: 0,
									width: '100%',
									maxWidth: '100%',
								},
							}}
						>
							<Grid item xs={6}>
								<div className="card alerts-dashboard" style={{ height: '100%' }}>
									<div className="events-header">
										<Typography
											variant="h6"
											sx={{ py: 2, m: 0, fontWeight: 500, fontSize: '20px' }}
										>
											Mabel A.I. - Local Events
										</Typography>
									</div>
									<LocalEvents workplaceId={workplaceId} onEventClick={handleLocalEventClick} />
								</div>
							</Grid>
							<Grid item xs={6}>
								<HotGames onGameClick={handleHotGameClick} />
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</div>

			{/* Middle Section */}
			<div className="middle-section">
				<TableContainer component={Paper} className="card">
					<Typography variant="h6" sx={{ p: 2, m: 0, fontWeight: 500 }}>
					All Outstanding Checklists
					</Typography>
					<Table stickyHeader>
						<TableHead>
							<TableRow>
								<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
									Checklist Name
								</TableCell>
								<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
									Assigned
								</TableCell>
								<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Owner</TableCell>
								<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
									Checklist Due
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{loading ? (
								<TableRow>
									<TableCell colSpan={4} align="center">
										<CircularProgress size={20} />
									</TableCell>
								</TableRow>
							) : checklistsToShow.length > 0 ? (
								checklistsToShow.map((checklist) => (
									<TableRow
										key={checklist.id}
										sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
									>
										<TableCell>{checklist.checklistTemplate.label}</TableCell>
										<TableCell>{getChecklistAssignedDisplay(checklist)}</TableCell>
										<TableCell>{getChecklistOwnerDisplay(checklist)}</TableCell>
									<TableCell>
										{checklist.scheduledDateFormatted && checklist.scheduledTimeFormatted
											? formatDateTime12Hour(checklist.scheduledDateFormatted, checklist.scheduledTimeFormatted)
											: formatDateTime(checklist.scheduledDate)}
									</TableCell>
									</TableRow>
								))
							) : (
								<TableRow>
									<TableCell colSpan={4} align="center">
										No outstanding checklists
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>

				<TableContainer component={Paper} className="card">
					<Typography variant="h6" sx={{ p: 2, m: 0, fontWeight: 500 }}>
					All Outstanding To-Dos
					</Typography>
					<Table stickyHeader>
						<TableHead>
							<TableRow>
								<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
									Description
								</TableCell>
								<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
									{' '}
									Assigned
								</TableCell>
								<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
									Status
								</TableCell>
								<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
									{' '}
									Due Date
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{loading ? (
								<TableRow>
									<TableCell colSpan={4} align="center">
										<CircularProgress size={20} />
									</TableCell>
								</TableRow>
						) : allOutstandingTodos.length > 0 ? (
							allOutstandingTodos.map((todo) => (
									<TableRow
										key={todo.id}
										sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
									>
										<TableCell>{getDescriptionDisplay(todo)}</TableCell>
										<TableCell>{getTodoAssignedDisplay(todo)}</TableCell>
										<TableCell>{getTodoStatusDisplay(todo)}</TableCell>
										<TableCell>{formatTodoDateWithTime(todo)}</TableCell>
									</TableRow>
								))
							) : (
								<TableRow>
									<TableCell colSpan={4} align="center">
										No outstanding to-dos
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
			</div>
		</div>
	);
};

export default Dashboard;
